import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import Box from "@material-ui/core/Box";
import UserManagementDetailPage from "./UserManagementDetailPage";
import UserManagementBaseClients from "./UserManagementBaseClients";
import { getUserRoles } from "../../Services/API/UserManagement";
import { getClients } from "../../Services/API/BaseClientManagement";
import { useLocation } from "react-router-dom";
import Breadcrumb from "../../Components/Navigation/Breadcrumbs/Breadcrumbs";

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography component={"span"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    backgroundColor: "transparent",
    boxShadow: "none",
    color: theme.palette.primary.main,
    fontWeight: "bold",
    width: "85%",
    zIndex: 0,
  },
  tab: {
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      "& .MuiBox-root": {
        padding: "0px",
      },
    },
  },
  largeIcon: {
    color: theme.palette.primary.main,
    margin: "auto",
    position: "absolute",
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  },
  tabContainer: {
    marginLeft: "24px",
  },
  primary: {
    backgroundColor: theme.palette.primary.main,
  },
}));

export default function UserTabView() {
  const classes = useStyles();
  const [value, setValue] = React.useState(0);

  const location = useLocation();
  const [assignedRoles, setAssignedRoles] = useState([]);
  const [unassignedBaseClients, setUnassignedBaseClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [componentMounted, setComponentMounted] = useState(true);

  const zipData = useCallback((baseClients, roles) => {
    // Filter out any base clients that do not have either Careers or CRM enabled
    for (const c in baseClients) {
      if (!baseClients[c].hasCareers && !baseClients[c].hasCrm) {
        baseClients.splice(c, 1);
      }
    }

    // Get all UserClientRoles assigned to a user and return the Base Client information for that UCR
    const baseClientAssignedRoles = [];
    for (const r in roles) {
      for (const c in baseClients) {
        if (
          baseClients[c].id === roles[r].baseClientID &&
          (roles[r].roleId !== null || (baseClients[c].hasCrm && roles[r].crmRole !== 0))
        ) {
          baseClientAssignedRoles.push({
            baseClient: baseClients[c],
            userClientRole: roles[r],
          });
          baseClients.splice(c, 1);
        }
      }
    }
    baseClientAssignedRoles.sort((a, b) => {
      const order = compare(a.baseClient.clientName, b.baseClient.clientName);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    setAssignedRoles(baseClientAssignedRoles);
    setUnassignedBaseClients(baseClients);
  }, []);

  const populate = useCallback(() => {
    if (location.state === undefined) return;
    getClients()
      .then((res) => {
        res.sort((a, b) => {
          const order = compare(a.clientName, b.clientName);
          if (order !== 0) return order;
          return a[1] - b[1];
        });
        return getUserRoles(location.state.id).then((response) => {
          if (componentMounted) {
            zipData(res, response);
          }
        });
      })
      .catch((err) => {
        console.error("Failed to load user base clients:", err);
      })
      .finally(() => {
        if (componentMounted) setLoading(false);
      });
  }, [componentMounted, location.state, zipData]);

  useEffect(() => {
    populate();

    return () => {
      setComponentMounted(false);
    };
  }, [populate]);

  function compare(a, b) {
    if (a < b) {
      return -1;
    }
    if (a > b) {
      return 1;
    }
    return 0;
  }

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  return (
    <div className="page-container">
      <Breadcrumb />
      <AppBar position="static" className={classes.root}>
        <Tabs
          value={value}
          onChange={handleChange}
          classes={{ indicator: classes.primary }}
          className={classes.tabContainer}
        >
          <Tab label="Profile" {...a11yProps(0)} className={classes.tab} />
          <Tab label="Base Clients" {...a11yProps(1)} className={classes.tab} />
        </Tabs>
      </AppBar>
      <TabPanel value={value} index={0} className={classes.tab}>
        <UserManagementDetailPage />
      </TabPanel>
      <TabPanel value={value} index={1} className={classes.tab}>
        <UserManagementBaseClients
          assignedRoles={assignedRoles}
          unassignedBaseClients={unassignedBaseClients}
          populate={populate}
          loading={loading}
        />
      </TabPanel>
    </div>
  );
}
